import React from "react";

// Default true: a tree mounted without the provider (tests, isolated mounts)
// has no splash to wait on, so gated autofocus behaves like plain autoFocus.
const SplashDoneContext = React.createContext(true);

export const SplashDoneProvider = SplashDoneContext.Provider;

/**
 * autoFocus that waits for the boot splash. A screen mounted under the splash
 * overlay is invisible but live: a plain `autoFocus` takes anyway, and Chrome
 * anchors its native autofill dropdown to the focused field — a popup that
 * paints above the overlay, floating over an apparently blank screen. The
 * returned ref focuses its element once, when the splash has cleared (which
 * on a post-splash mount is immediately).
 *
 * `enabled` mirrors a conditional `autoFocus={flag}`: the focus fires when
 * the flag and the splash are both settled.
 */
export function useSplashAutoFocus<T extends HTMLElement = HTMLInputElement>(
  enabled = true,
): React.RefObject<T | null> {
  const splashDone = React.useContext(SplashDoneContext);
  const ref = React.useRef<T>(null);
  const focused = React.useRef(false);

  React.useEffect(() => {
    if (!enabled || !splashDone || focused.current) return;
    const node = ref.current;
    if (!node) return;
    focused.current = true;
    node.focus();
  }, [enabled, splashDone]);

  return ref;
}
